import {
  type Form,
  type FormField,
  PriceType,
  type Validation,
  ValidationFormat,
} from '@wix/ambassador-forms-v4-form/types';
import {
  EMPTY_VALUE,
  FIELD_TYPES,
  type FormValues,
  getVatIDType,
  type InputFieldType,
  type PossibleValues,
  type VatIDValue,
  type MultilineAddressValue,
} from '@wix/form-fields';
import { MultilineAddressToolkit } from '@wix/form-multiline-address';

import { isNumber, isObject, isRating, isString } from '../../services/utils';
import { type FormConfig } from '../../types';

export const normalizeValues = (
  form?: Form,
  values?: FormValues,
  recursive = true,
  config?: FormConfig | null,
) => {
  if (!form?.fields) {
    return {};
  }

  const normalizedValues = form.fields.reduce<FormValues>(
    (result: FormValues, field: FormField) => {
      const fieldTarget = field.target;

      if (!fieldTarget) {
        return result;
      }

      const fieldValue = values?.[fieldTarget];
      const fieldType = field.view?.fieldType as InputFieldType;
      const nestedForm = field.view?.form;

      if (recursive && nestedForm) {
        result[fieldTarget] = normalizeValues(
          nestedForm,
          fieldValue as FormValues,
          false,
          config,
        ) as PossibleValues;
      } else {
        const normalizer = NORMALIZED_VALUE[fieldType];
        if (normalizer) {
          result[fieldTarget] = normalizer({
            field,
            fieldValue,
            config,
          }) as PossibleValues;
        } else {
          result[fieldTarget] = fieldValue as PossibleValues;
        }
      }

      return result;
    },
    {},
  );

  return normalizedValues;
};

export const NORMALIZED_VALUE = {
  [FIELD_TYPES.TEXT_INPUT]: acceptStringValue,
  [FIELD_TYPES.CHECKBOX_GROUP]: removeInvalidOptions,
  [FIELD_TYPES.RADIO_GROUP]: acceptOnlyValidOptionByLabel,
  [FIELD_TYPES.QUIZ_RADIO_GROUP]: acceptOnlyValidOptionByLabel,
  [FIELD_TYPES.DROPDOWN]: acceptOnlyValidOptionByLabel,
  [FIELD_TYPES.URL_INPUT]: acceptStringValue,
  [FIELD_TYPES.NUMBER_INPUT]: acceptNumberValue,
  [FIELD_TYPES.CHALLENGES_NUMBER]: acceptNumberValue,
  [FIELD_TYPES.CHALLENGES_SHORT_TEXT]: acceptStringValue,
  [FIELD_TYPES.CHALLENGES_LONG_TEXT]: acceptStringValue,
  [FIELD_TYPES.CHALLENGES_MULTI_CHOICE]: removeInvalidOptions,
  [FIELD_TYPES.CHALLENGES_IMAGE_CHOICE]: removeInvalidOptions,
  [FIELD_TYPES.CHALLENGES_SINGLE_CHOICE]: acceptOnlyValidOptionByLabel,
  [FIELD_TYPES.CHALLENGES_FILE_UPLOAD]: keepValueUnchanged,
  [FIELD_TYPES.QUIZ_TEXT_INPUT]: acceptStringValue,
  [FIELD_TYPES.QUIZ_NUMBER_INPUT]: acceptNumberValue,
  [FIELD_TYPES.QUIZ_CHECKBOX_GROUP]: removeInvalidOptions,
  [FIELD_TYPES.QUIZ_IMAGE_CHOICE]: removeInvalidOptions,
  [FIELD_TYPES.FILE_UPLOAD]: keepValueUnchanged,
  [FIELD_TYPES.SIGNATURE]: keepValueUnchanged,
  [FIELD_TYPES.CONTACTS_EMAIL]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_PHONE]: acceptPhoneValue,
  [FIELD_TYPES.CHECKBOX]: acceptBooleanValue,
  [FIELD_TYPES.DATE_TIME_INPUT]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_COMPANY]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_POSITION]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_TAX_ID]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_FIRST_NAME]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_LAST_NAME]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_ADDRESS]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_BIRTHDATE]: acceptStringValue,
  [FIELD_TYPES.TEXT_AREA]: acceptStringValue,
  [FIELD_TYPES.CONTACTS_SUBSCRIBE]: acceptBooleanValue,
  [FIELD_TYPES.DATE_INPUT]: acceptStringValue,
  [FIELD_TYPES.TIME_INPUT]: acceptStringValue,
  [FIELD_TYPES.DATE_PICKER]: acceptStringValue,
  [FIELD_TYPES.NESTED_FORM]: acceptObjectValue,
  [FIELD_TYPES.MULTILINE_ADDRESS]: normalizeMultilineAddress,
  [FIELD_TYPES.MLA_COUNTRY]: acceptOnlyValidOptionByValue,
  [FIELD_TYPES.MLA_CITY]: acceptStringValue,
  [FIELD_TYPES.MLA_ADDRESS_LINE]: acceptStringValue,
  [FIELD_TYPES.MLA_ADDRESS_LINE_2]: acceptStringValue,
  [FIELD_TYPES.MLA_POSTAL_CODE]: acceptStringValue,
  [FIELD_TYPES.MLA_SUBDIVISION]: acceptOnlyValidOptionByValue,
  [FIELD_TYPES.MLA_STREET_NAME]: acceptStringValue,
  [FIELD_TYPES.MLA_STREET_NUMBER]: acceptStringValue,
  [FIELD_TYPES.MLA_APARTMENT]: acceptStringValue,
  [FIELD_TYPES.FULL_NAME]: acceptFullNameValue,
  [FIELD_TYPES.FULL_NAME_FIRST_NAME]: acceptStringValue,
  [FIELD_TYPES.FULL_NAME_LAST_NAME]: acceptStringValue,
  [FIELD_TYPES.VAT_ID]: normalizeVatIDValue,
  [FIELD_TYPES.DEXT_TEXT_INPUT]: acceptStringValue,
  [FIELD_TYPES.DEXT_NUMBER_INPUT]: acceptNumberValue,
  [FIELD_TYPES.DEXT_CHECKBOX]: acceptBooleanValue,
  [FIELD_TYPES.DEXT_CHECKBOX_GROUP]: removeInvalidOptions,
  [FIELD_TYPES.DEXT_RATING_INPUT]: acceptRatingValue,
  [FIELD_TYPES.DEXT_TAGS]: removeInvalidOptions,
  [FIELD_TYPES.ECOM_ADDITIONAL_INFO]: acceptStringValue,
  [FIELD_TYPES.ECOM_ADDRESS]: acceptObjectValue,
  [FIELD_TYPES.ECOM_PHONE]: acceptPhoneValue,
  [FIELD_TYPES.ECOM_FULL_NAME]: acceptObjectValue,
  [FIELD_TYPES.ECOM_COMPANY_NAME]: acceptStringValue,
  [FIELD_TYPES.ECOM_SUBSCRIPTION]: acceptBooleanValue,
  [FIELD_TYPES.ECOM_EMAIL]: acceptStringValue,
  [FIELD_TYPES.DEXT_TEXT_AREA]: acceptStringValue,
  [FIELD_TYPES.DEXT_DROPDOWN]: acceptOnlyValidOptionByLabel,
  [FIELD_TYPES.DEXT_RADIO_GROUP]: acceptOnlyValidOptionByLabel,
  [FIELD_TYPES.DEXT_URL_INPUT]: acceptStringValue,
  [FIELD_TYPES.DEXT_EMAIL]: acceptStringValue,
  [FIELD_TYPES.DEXT_PHONE]: acceptPhoneValue,
  [FIELD_TYPES.DEXT_DATE_PICKER]: acceptStringValue,
  [FIELD_TYPES.DEXT_DATE_TIME_INPUT]: acceptStringValue,
  [FIELD_TYPES.PRODUCT_LIST]: acceptOnlyValidProducts,
  [FIELD_TYPES.DONATION]: acceptOnlyValidProducts,
  [FIELD_TYPES.RATING_INPUT]: acceptRatingValue,
  [FIELD_TYPES.TAGS]: removeInvalidOptions,
  [FIELD_TYPES.PAYMENT_INPUT]: acceptOnlyValidPayments,
  [FIELD_TYPES.FIXED_PAYMENT]: acceptOnlyValidPayments,
  [FIELD_TYPES.BOOKINGS_FIRST_NAME]: acceptStringValue,
  [FIELD_TYPES.BOOKINGS_LAST_NAME]: acceptStringValue,
  [FIELD_TYPES.BOOKINGS_EMAIL]: acceptStringValue,
  [FIELD_TYPES.BOOKINGS_PHONE]: acceptPhoneValue,
  [FIELD_TYPES.BOOKINGS_ADDRESS]: normalizeMultilineAddress,
  [FIELD_TYPES.APPOINTMENT]: acceptObjectValue,
  [FIELD_TYPES.PASSWORD]: acceptStringValue,
  [FIELD_TYPES.SERVICES_DROPDOWN]: acceptStringValue,
  [FIELD_TYPES.SERVICES_MULTI_CHOICE]: removeInvalidOptions,
  [FIELD_TYPES.EVENTS_RSVP]: acceptOnlyValidOptionByValue,
  [FIELD_TYPES.EVENTS_REPEATER]: normalizeRepeater,
  [FIELD_TYPES.IMAGE_CHOICE]: removeInvalidOptions,
  [FIELD_TYPES.PHONE]: acceptPhoneValue,
};

interface KeepValueUnchanged {
  field: FormField;
  fieldValue?: PossibleValues | Record<string, PossibleValues>;
  config?: FormConfig | null;
}

function keepValueUnchanged({ fieldValue }: KeepValueUnchanged) {
  return fieldValue;
}

function acceptPhoneValue({ fieldValue }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (isEmptyValue) {
    return fieldValue;
  }

  if (typeof fieldValue === 'string') {
    const stripped = fieldValue.replace(/[^0-9+]/g, '');
    return stripped || fieldValue;
  }

  return EMPTY_VALUE;
}

function removeInvalidOptions({ field, fieldValue }: KeepValueUnchanged) {
  const hasAddOther = field.view?.addOther;
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (hasAddOther || isEmptyValue) {
    return fieldValue;
  }

  const options = field.view?.options;

  if (!Array.isArray(fieldValue)) {
    return EMPTY_VALUE;
  }

  const value = (fieldValue as string[])?.filter((arrayItem: string) =>
    options.some(
      (option: { label: string; value?: string }) =>
        option.value === arrayItem || option.label === arrayItem,
    ),
  );

  return value.length ? value : EMPTY_VALUE;
}

function acceptOnlyValidOptionByLabel({
  field,
  fieldValue,
}: KeepValueUnchanged) {
  const hasAddOther = field.view?.addOther;
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (hasAddOther || isEmptyValue) {
    return fieldValue;
  }

  const options = field.view?.options;

  const optionExists = options.some(
    (option: { label: string; value?: string }) =>
      option.value === fieldValue || option.label === fieldValue,
  );
  return optionExists ? fieldValue : EMPTY_VALUE;
}

function acceptOnlyValidProducts({ field, fieldValue }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (isEmptyValue) {
    return fieldValue;
  }

  if (!Array.isArray(fieldValue)) {
    return EMPTY_VALUE;
  }

  const validation: Validation | undefined = field.validation;
  if (validation?.predefined?.format !== ValidationFormat.PAYMENT) {
    return EMPTY_VALUE;
  }

  const validationProducts =
    validation.predefined.paymentOptions?.products || [];
  if (validationProducts.length === 0) {
    return EMPTY_VALUE;
  }

  const validProducts = (fieldValue as any[]).filter((productValue: any) => {
    const hasAllProps =
      isObject(productValue) &&
      productValue.hasOwnProperty('productId') &&
      productValue.hasOwnProperty('price') &&
      productValue.hasOwnProperty('quantity');

    if (!hasAllProps) {
      return false;
    }

    const price = productValue.price;
    const isValidPrice = isString(price)
      ? Number(price).toString() === price
      : isNumber(price);
    if (!isValidPrice) {
      return false;
    }

    const isValidQuantity = isNumber(productValue.quantity);
    if (!isValidQuantity) {
      return false;
    }

    const isExisted = validationProducts.some(
      (product) => product.id === productValue.productId,
    );
    return isExisted;
  });

  return validProducts.length ? validProducts : EMPTY_VALUE;
}

function acceptOnlyValidOptionByValue({
  field,
  fieldValue,
}: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (isEmptyValue) {
    return fieldValue;
  }

  type Option = { value: string };
  const options: Option[] | undefined = field.view?.options;

  if (!options) {
    return EMPTY_VALUE;
  }

  const optionExists = options.some((option) => option.value === fieldValue);
  return optionExists ? fieldValue : EMPTY_VALUE;
}

function normalizeVatIDValue({ fieldValue }: KeepValueUnchanged) {
  const vatID = fieldValue as VatIDValue;

  const isEmptyValue = fieldValue === EMPTY_VALUE;

  if (isEmptyValue) {
    return fieldValue;
  }

  if (fieldValue && isObject(fieldValue) && fieldValue.hasOwnProperty('id')) {
    if (fieldValue.hasOwnProperty('type')) {
      const type = getVatIDType(vatID.id);
      return { id: vatID.id, type };
    }

    return { id: vatID.id };
  }

  return EMPTY_VALUE;
}

function acceptNumberValue({ fieldValue }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (isEmptyValue) {
    return fieldValue;
  }

  return isNumber(fieldValue) ? fieldValue : EMPTY_VALUE;
}

function acceptStringValue({ fieldValue }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (isEmptyValue) {
    return fieldValue;
  }

  return isString(fieldValue) ? fieldValue : EMPTY_VALUE;
}

function acceptBooleanValue({ fieldValue, field }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (isEmptyValue) {
    return fieldValue;
  }

  if (
    fieldValue === false &&
    field.validation?.required &&
    field.view?.defaultValue === false &&
    field.hidden
  ) {
    return EMPTY_VALUE;
  }

  const isBoolean = typeof fieldValue === 'boolean';

  return isBoolean ? fieldValue : EMPTY_VALUE;
}

function normalizeMultilineAddress({
  fieldValue,
  config,
  field,
}: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (isEmptyValue) {
    return EMPTY_VALUE;
  }

  const addressForms = config?.addressTemplates?.map(
    (template) => template.template,
  );

  if (!addressForms) {
    return EMPTY_VALUE;
  }

  const addressToolkit = new MultilineAddressToolkit(addressForms as Form[]);
  const country = (fieldValue as MultilineAddressValue)?.country;
  const template = addressToolkit.getFormByCountry(country, field);
  return normalizeValues(template, fieldValue as FormValues, true, config);
}

function acceptObjectValue({ fieldValue }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  if (isEmptyValue) {
    return fieldValue;
  }

  return typeof fieldValue === 'object' ? fieldValue : EMPTY_VALUE;
}

function acceptFullNameValue(args: KeepValueUnchanged) {
  const result = acceptObjectValue(args);
  return result === EMPTY_VALUE ? {} : result;
}

function acceptRatingValue({ fieldValue }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === 0 || fieldValue === EMPTY_VALUE;

  if (isEmptyValue) {
    return EMPTY_VALUE;
  }

  return isRating(fieldValue) ? fieldValue : EMPTY_VALUE;
}

function acceptOnlyValidPayments({ field, fieldValue }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;

  const { validation } = field;
  const payment = validation?.predefined?.paymentOptions?.products?.[0];

  if (isEmptyValue && payment?.priceType === PriceType.FIXED_PRICE) {
    return [
      {
        productId: payment.id,
        price: payment.fixedPriceOptions?.price,
        quantity: 1,
      },
    ];
  }

  if (isEmptyValue) {
    return fieldValue;
  }

  if (!Array.isArray(fieldValue)) {
    return EMPTY_VALUE;
  }

  return fieldValue;
}

function normalizeRepeater({ field, fieldValue, config }: KeepValueUnchanged) {
  const isEmptyValue = fieldValue === EMPTY_VALUE;
  const nestedFields = field.view?.fields;

  if (isEmptyValue) {
    return fieldValue;
  }

  if (!Array.isArray(fieldValue) || !nestedFields) {
    return EMPTY_VALUE;
  }

  const nestedForm: Form = {
    fields: nestedFields,
  };

  const normalizedValues = (fieldValue as FormValues[]).map((values) => {
    if (!isObject(values)) {
      return {};
    }

    const normalized = normalizeValues(
      nestedForm,
      values as FormValues,
      false,
      config,
    );

    return Object.fromEntries(
      Object.entries(normalized).filter(([_, value]) => value !== EMPTY_VALUE),
    );
  });

  return normalizedValues;
}
